/**
 * 인포트렌드 원가표 API — backend /price-list/infortrend/* 와 1:1.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

export interface InfortrendMeta {
  id: number;
  is_current?: boolean;
  quarter_label: string;
  validity_date: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
  row_count: number;
  sheet_count: number;
}

export interface InfortrendRow {
  cat: string;
  pn: string;
  model: string | null;
  family: string | null;
  series: string | null;
  subsystem: string | null;
  form: string | null;
  drive: string | null;
  dram: string | null;
  description: string;
  status: string | null;
  extra: Record<string, string> | null;
  supply: number;
  retail: number;
  change_pct: number | null;
  sheet_name: string;
  sheet_row: number;
}

export interface InfortrendListResult {
  meta: InfortrendMeta | null;
  rows: InfortrendRow[];
}

export async function listInfortrendPrice(metaId?: number | null): Promise<InfortrendListResult> {
  const qs = metaId ? `?metaId=${metaId}` : '';
  const res = await fetch(`${BASE}/price-list/infortrend/list${qs}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<InfortrendListResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function listInfortrendMetas(): Promise<{ metas: InfortrendMeta[] }> {
  const res = await fetch(`${BASE}/price-list/infortrend/meta/list`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ metas: InfortrendMeta[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface InfortrendUploadResult {
  rowCount: number;
  sheetCount: number;
  validityDate: string | null;
  quarterLabel: string;
  metaId: number;
  warnings: string[];
}

export async function uploadInfortrendXlsx(
  file: File,
  quarterLabel: string,
  validityDate: string,  // 'YYYY-MM-DD' (빈 문자열이면 엑셀에서 추출한 값 사용)
): Promise<InfortrendUploadResult> {
  const fd = new FormData();
  fd.append('quarterLabel', quarterLabel);
  if (validityDate) fd.append('validityDate', validityDate);
  fd.append('file', file);

  const res = await fetch(`${BASE}/price-list/infortrend/upload`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = (await res.json()) as ApiResponse<InfortrendUploadResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function deleteInfortrendMeta(id: number): Promise<{ deletedId: number; quarterLabel: string }> {
  const res = await fetch(`${BASE}/price-list/infortrend/meta/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ deletedId: number; quarterLabel: string }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/* ── 품목 찾기 — 딜등록 「P/N (제안 품목)」 고르기 (v2.151.0) ── */

/** 가격표 한 줄 (검색 결과) — 원가가 실리므로 영업 이상만 부른다 */
export interface InfortrendSearchRow {
  id: number;
  cat: string | null;
  pn: string | null;
  model: string | null;
  series: string | null;
  form: string | null;
  drive: string | null;
  dram: string | null;
  description: string | null;
  status: string | null;
  supply: number | null;
  retail: number | null;
  /** 그 줄이 실린 분기 (예: 2026 Q3) */
  quarter_label: string;
  /** 현재 분기 가격표인가 */
  is_current: boolean;
  /** 서버가 매긴 일치 점수 — 모델 정확 100 · 모델 앞부분 80 · P/N 앞부분 70 … */
  score: number;
}

export interface InfortrendSearchResult {
  rows: InfortrendSearchRow[];
  scope: 'current' | 'all';
  /** 현재 분기에서 못 찾아 옛 분기까지 뒤졌으면 true */
  searchedAll: boolean;
}

/** 품목 묶음 — ① 본체(필수) · ② 호스트보드 · ③ 하드디스크 · ④ 기타 (v2.152.0) */
export interface PartGroup {
  key: 'main' | 'host' | 'hdd' | 'etc';
  label: string;
  required: boolean;
  hint: string;
  count: number;
  /** 그 묶음 안의 제품군(가격표 시트) — 「전체」는 화면이 붙인다 */
  cats: { cat: string; label: string; count: number }[];
}

/**
 * 「본체가 적혀 있나」 판정 글자 — **서버가 내려 준다**(v2.160.0).
 * 규칙은 서버 `lib/price-list/part-groups.ts` 한 곳에만 있다. 화면에 베끼지 말 것.
 */
export interface MainPartRule {
  /** 「GS 3060RT3-E」처럼 띄어 쓴 모델명을 붙이는 자리 */
  gluePattern: string;
  /** 본체로 볼 토큰의 생김새 */
  tokenPattern: string;
  /** 토큰 최소 길이 */
  minLen: number;
}

/**
 * P/N 칸 글자에 본체로 보이는 품목이 하나라도 있나 — 서버가 준 규칙으로만 판정한다.
 * 규칙을 못 받았으면(권한·통신 실패) **참으로 본다** — 확실하지 않을 때 잔소리하지 않는다.
 */
export function hasMainPart(pnText: string, rule: MainPartRule | undefined): boolean {
  if (!rule) return true;
  const up = (pnText ?? '').toUpperCase();
  if (!up.trim()) return false;
  try {
    const glued = up.replace(new RegExp(rule.gluePattern, 'g'), (m) => m.replace(/[ \t]+/g, ''));
    const re = new RegExp(rule.tokenPattern);
    return glued.split(/[^0-9A-Z-]+/).some((t) => t.length >= rule.minLen && re.test(t));
  } catch {
    return true; // 규칙 글자가 이상하면 조용히 넘어간다 (저장을 막는 자리가 아니다)
  }
}

/** 묶음·제품군 목록 (현재 분기 기준). 어느 분류가 어느 묶음인지는 **서버가 정한다** */
export async function listPartGroups(): Promise<{ groups: PartGroup[]; mainPart?: MainPartRule }> {
  const res = await fetch(`${BASE}/price-list/infortrend/part-groups`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ groups: PartGroup[]; mainPart?: MainPartRule }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/**
 * 가격표에서 품목 찾기. 띄어쓰기·대소문자는 서버가 맞춰 준다
 * (「gs 3060rt3-e」 로 적어도 「GS 3060RT3-E」 를 찾는다).
 * v2.152.0: **검색어 없이 묶음·제품군만으로도** 목록을 볼 수 있다(전체를 뒤지면 너무 많다).
 */
export async function searchInfortrendParts(
  q: string,
  opts?: { limit?: number; scope?: 'current' | 'all'; group?: PartGroup['key']; cat?: string },
): Promise<InfortrendSearchResult> {
  const p = new URLSearchParams();
  if (q.trim()) p.set('q', q.trim());
  if (opts?.group) p.set('group', opts.group);
  if (opts?.cat) p.set('cat', opts.cat);
  if (opts?.limit) p.set('limit', String(opts.limit));
  if (opts?.scope) p.set('scope', opts.scope);
  const res = await fetch(`${BASE}/price-list/infortrend/search?${p.toString()}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<InfortrendSearchResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}
